import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import {
    Banknote,
    Check,
    Clock,
    CreditCard,
    DollarSign,
    ExternalLink,
    Image as ImageIcon,
    QrCode,
    Receipt,
    ShoppingCart,
    Tag,
    Undo2,
    User,
    X,
    XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { getSaleResumeFinalAmount, hasNonZeroProductItemDiscount } from '../../common/saleUtils';
import StatusChip from '../common/StatusChip';

// Drawer de preview (somente leitura) da venda, aberto a partir da linha do
// Relatório de Vendas (`pages/reports/SalesReport.js`) — DESIGN-SYSTEM.md
// §5.6 (side drawer) + mesmo padrão já aprovado de drawer de
// visualização/preview usado em `pages/audit/AuditDetailDrawer.js` e
// `pages/suppliers/View.js` (header com ícone-chip + título/subtitle, corpo
// com cards neutros `background.subtle`, rodapé com "Fechar" + ação
// primária). Reaproveita, onde faz sentido, o mesmo vocabulário visual de
// `pages/sales/View.js` (`/sales/:id`, já redesenhado a partir de
// `prototipo-visualizacao-venda.html`): ícones/tons dos cards "Resumo
// financeiro"/"Pagamentos"/"Produtos" (`CardHeadRow`) e a lista de formas de
// pagamento (`PaymentRow`/`PAYMENT_META`) — sem reusar o header em gradiente
// de marca da página cheia, que é um padrão de página, não de drawer de
// preview.
//
// Substitui a implementação legada anterior (gradiente hardcoded, ícones
// MUI filled, `Chip` de status com cor local, scrollbar customizado inline)
// — mesma interface pública (`open`/`onClose`/`saleId`), mesmos 3 endpoints
// (`/sales/{id}/resume`, `/items`, `/payments`) e mesmos campos exibidos.

// Ícone/tom do chip do cabeçalho por status — mesmos 4 status reais de venda
// já usados em `sales/List.js`/`sales/View.js`/`StatusChip domain="sale"`.
const STATUS_META = {
    iniciada: { Icon: Clock, bg: 'info.bg', fg: 'info.main' },
    finalizada: { Icon: Check, bg: 'success.bg', fg: 'success.main' },
    cancelada: { Icon: XCircle, bg: 'error.bg', fg: 'error.main' },
    estornada: { Icon: Undo2, bg: 'warning.bg', fg: 'warning.main' },
};

function normalizeStatus(status) {
    return (status || '').trim().toLowerCase();
}

// `.card-head` condensado — mesmo padrão de `pages/sales/View.js` (`CardHeadRow`).
function CardHeadRow({ icon: Icon, iconBg, iconFg, title }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: '14px' }}>
            <Box sx={{
                width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: iconBg, color: iconFg,
            }}>
                <Icon size={16} strokeWidth={2} />
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
        </Box>
    );
}

function FieldCard({ children, sx }) {
    return (
        <Box sx={{ backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: '16px', mb: '14px', '&:last-child': { mb: 0 }, ...sx }}>
            {children}
        </Box>
    );
}

// Par label/valor read-only — mesmo padrão `RoField` de `pages/suppliers/View.js`/`pages/vehicles/View.js`.
function RoField({ label, value, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: value ? 'text.primary' : 'text.disabled', lineHeight: 1.4 }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

// `.summary-row` condensado — mesmo padrão `FinRow` de `pages/sales/View.js`.
function FinRow({ icon: Icon, iconColor = 'error.main', label, value, valueColor = 'text.primary', valueWeight = 600, valueSize = '13px' }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '7px', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {Icon && (
                    <Box sx={{ color: iconColor, display: 'flex' }}>
                        <Icon size={12} strokeWidth={2} />
                    </Box>
                )}
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>{label}</Typography>
            </Box>
            <Typography sx={{ fontSize: valueSize, fontWeight: valueWeight, color: valueColor }}>{value}</Typography>
        </Box>
    );
}

// Mesma tabela de ícone/tom por forma de pagamento de `pages/sales/View.js`
// (`PAYMENT_META`) — mantém as chaves cruas da API (`CRÉDITO`/`DÉBITO`/
// `PIXKEY`/`PIXQRCODE`/`DINHEIRO`).
const PAYMENT_META = {
    'CRÉDITO': { icon: CreditCard, iconBg: 'secondary.bg', iconFg: 'secondary.main', label: 'Crédito' },
    'DÉBITO': { icon: CreditCard, iconBg: 'info.bg', iconFg: 'primary.main', label: 'Débito' },
    'PIXKEY': { icon: QrCode, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX' },
    'PIXQRCODE': { icon: QrCode, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX QRCode' },
    'DINHEIRO': { icon: Banknote, iconBg: 'success.bg', iconFg: 'success.main', label: 'Dinheiro' },
};

function PaymentRow({ payment }) {
    const meta = PAYMENT_META[payment.method] || { icon: CreditCard, iconBg: 'background.paper', iconFg: 'text.disabled', label: payment.method };
    const Icon = meta.icon;
    const hasChange = payment.method === 'DINHEIRO' && payment.cash_change != null && payment.cash_change !== '' && payment.cash_change !== '0,00';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '11px 12px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '10px', mb: '8px', '&:last-child': { mb: 0 } }}>
            <Box sx={{
                width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: meta.iconBg, color: meta.iconFg,
            }}>
                <Icon size={14} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{meta.label}</Typography>
                {hasChange && (
                    <Typography sx={{ fontSize: '11px', color: 'warning.main', fontWeight: 600, mt: '1px' }}>
                        Troco: R$ {payment.cash_change}
                    </Typography>
                )}
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: 'success.main', flexShrink: 0 }}>
                R$ {payment.amount}
            </Typography>
        </Box>
    );
}

export default function SaleViewSheet({ open, onClose, saleId }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sale, setSale] = useState({
        number: '',
        status: '',
        date: '',
        user: '',
        total_amount: '0,00',
        total_discount: '0,00',
        total_discount_applied: '0,00',
        total_percent_tax: '0',
        total_tax_machine: '0,00',
        total_discount_tax: '0,00',
        cash_register_name: '',
        reason_cancel: '',
        items_gross_subtotal: '',
        total_item_discount: '',
        net_after_item_discounts: '',
        total_discounts_combined: '',
        final_total: ''
    });
    const [saleItems, setSaleItems] = useState([]);
    const [salePayments, setSalePayments] = useState([]);

    useEffect(() => {
        if (!open || !saleId) return;

        const load = async () => {
            setLoading(true);
            try {
                const [resumeRes, itemsRes, paymentsRes] = await Promise.all([
                    httpClient.get(`/sales/${saleId}/resume`),
                    httpClient.get(`/sales/${saleId}/items`),
                    httpClient.get(`/sales/${saleId}/payments`)
                ]);
                setSale(resumeRes.data || {});
                const rawItems = itemsRes.data;
                const itemsList = Array.isArray(rawItems) ? rawItems : rawItems?.items ?? [];
                setSaleItems(itemsList);
                setSalePayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
            } catch (err) {
                console.error('Erro ao carregar venda:', err);
                setSale({});
                setSaleItems([]);
                setSalePayments([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, saleId]);

    const handleOpenFullPage = () => {
        onClose();
        navigate(`/sales/${saleId}`);
    };

    const statusMeta = STATUS_META[normalizeStatus(sale.status)] || { Icon: Receipt, bg: 'background.subtle', fg: 'text.secondary' };
    const totalUnits = saleItems.reduce((acc, i) => acc + (i.quantity || 0), 0);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 480, md: 520 },
                    maxWidth: '100vw',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* drawer-head */}
            <Box sx={{ p: '20px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: statusMeta.bg, color: statusMeta.fg, mb: '10px',
                    }}>
                        <statusMeta.Icon size={18} strokeWidth={2} />
                    </Box>
                    {loading ? (
                        <Skeleton variant="text" width={140} height={28} />
                    ) : (
                        <Typography sx={{ fontSize: '17px', fontWeight: 800, letterSpacing: '-0.01em', mb: '6px' }}>
                            Venda #{sale.number || '—'}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                        {loading ? (
                            <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: '20px' }} />
                        ) : (
                            <StatusChip domain="sale" value={sale.status} />
                        )}
                        {!loading && sale.date && (
                            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>{sale.date}</Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* drawer-body */}
            <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <Skeleton variant="rounded" height={110} sx={{ borderRadius: '12px' }} />
                        <Skeleton variant="rounded" height={160} sx={{ borderRadius: '12px' }} />
                        <Skeleton variant="rounded" height={90} sx={{ borderRadius: '12px' }} />
                    </Box>
                ) : (
                    <>
                        {sale.status === 'Cancelada' && sale.reason_cancel && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', p: '12px 14px', borderRadius: '12px', mb: '14px', bgcolor: 'error.bg', border: '1px solid', borderColor: 'error.main' }}>
                                <Box sx={{ color: 'error.main', display: 'flex', flexShrink: 0, mt: '2px' }}>
                                    <XCircle size={16} strokeWidth={2} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'error.main', mb: '2px' }}>
                                        Motivo do cancelamento
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'error.main' }}>
                                        {sale.reason_cancel}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Informações */}
                        <FieldCard>
                            <CardHeadRow icon={User} iconBg="secondary.bg" iconFg="secondary.main" title="Informações" />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                                <RoField label="Data" value={sale.date} />
                                <RoField label="Vendedor" value={sale.user} />
                                {sale.cash_register_name && <RoField full label="Caixa" value={sale.cash_register_name} />}
                            </Box>
                        </FieldCard>

                        {/* Resumo financeiro */}
                        <FieldCard>
                            <CardHeadRow icon={DollarSign} iconBg="success.bg" iconFg="success.main" title="Resumo financeiro" />
                            <FinRow
                                label="Bruto (itens)"
                                value={`R$ ${sale.items_gross_subtotal !== undefined && sale.items_gross_subtotal !== '' ? sale.items_gross_subtotal : (sale.total_amount || '0,00')}`}
                            />
                            <FinRow
                                label="Desc. produtos"
                                value={`− R$ ${sale.total_item_discount !== undefined && sale.total_item_discount !== '' ? sale.total_item_discount : '0,00'}`}
                                valueColor="error.main"
                            />
                            {hasNonZeroProductItemDiscount(sale) && (
                                <FinRow
                                    label="Líquido após itens"
                                    value={`R$ ${sale.net_after_item_discounts !== undefined && sale.net_after_item_discounts !== '' ? sale.net_after_item_discounts : (sale.total_amount || '0,00')}`}
                                />
                            )}
                            <Divider sx={{ my: '2px' }} />
                            <FinRow
                                icon={Tag}
                                label="Desc. venda"
                                value={`− R$ ${sale.total_discount_applied !== undefined && sale.total_discount_applied !== '' ? sale.total_discount_applied : '0,00'}`}
                                valueColor="error.main"
                            />
                            {(sale.total_discounts_combined !== undefined && sale.total_discounts_combined !== '') && (
                                <FinRow
                                    label="Total descontos"
                                    value={`− R$ ${sale.total_discounts_combined}`}
                                    valueColor="error.main"
                                />
                            )}
                            <Divider sx={{ my: '2px' }} />
                            <FinRow
                                label="Valor final"
                                value={`R$ ${getSaleResumeFinalAmount(sale)}`}
                                valueColor="success.main"
                                valueWeight={800}
                                valueSize="15px"
                            />
                            <FinRow
                                label={`Taxa (${sale.total_percent_tax}%)`}
                                value={`R$ ${sale.total_tax_machine !== '0,00' ? sale.total_tax_machine : '0,00'}`}
                            />
                            <FinRow
                                label="Líquido"
                                value={`R$ ${sale.total_discount_tax !== '0,00' ? sale.total_discount_tax : '0,00'}`}
                                valueWeight={700}
                            />
                        </FieldCard>

                        {/* Produtos */}
                        <FieldCard>
                            <CardHeadRow icon={ShoppingCart} iconBg="info.bg" iconFg="primary.main" title={`Produtos (${totalUnits} un.)`} />
                            <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                                {saleItems.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            p: '9px 10px', borderRadius: '10px', mb: '8px', '&:last-child': { mb: 0 },
                                            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                                        }}
                                    >
                                        {item.image ? (
                                            <Box component="img" src={item.image} alt="" sx={{ width: 38, height: 38, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                                        ) : (
                                            <Box sx={{ width: 38, height: 38, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.subtle', color: 'text.disabled' }}>
                                                <ImageIcon size={18} strokeWidth={1.6} />
                                            </Box>
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>
                                                {item.subtotal != null && item.subtotal !== ''
                                                    ? `Subtotal R$ ${item.subtotal}`
                                                    : `${item.quantity} × R$ ${item.price}`}
                                                {item.item_discount_amount ? ` · Desc. −R$ ${item.item_discount_amount}` : ''}
                                                {` · Total R$ ${item.total}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </FieldCard>

                        {/* Pagamentos */}
                        <FieldCard sx={{ mb: 0 }}>
                            <CardHeadRow icon={CreditCard} iconBg="warning.bg" iconFg="warning.main" title="Pagamentos" />
                            {salePayments.map((payment, idx) => (
                                <PaymentRow key={idx} payment={payment} />
                            ))}
                        </FieldCard>
                    </>
                )}
            </Box>

            {/* drawer-foot */}
            <Box sx={{ p: '16px 22px 20px', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
                <Button variant="outlined" onClick={onClose}>Fechar</Button>
                <Button variant="contained" endIcon={<ExternalLink size={15} strokeWidth={2} />} onClick={handleOpenFullPage}>
                    Ver mais
                </Button>
            </Box>
        </Drawer>
    );
}
